import { useEffect, useState } from "react";
import LiteratureReviewLayout from "../../Components/LiteratureReviewLayout/LiteratureReviewLayout";
import { useLiteratureReview } from "../../Context/LiteratureReviewContext";
import "./PaperSources.css";
import { AddFromSearch } from "../../Components/AddFromSearch/AddFromSearch";
import {
  fetchSources,
  saveAddedArticles,
  proceedLargeSearch,
} from "../../Utils/functions";
import { CircularProgress, IconButton } from "@mui/material";
import { MenuBook as MenuBookIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { DoiButton } from "../../Components/DoiButton/DoiButton";

const PaperSources = () => {
  const { id, workflowData, setWorkflowData, navigateToStep, getStepsForPage } = useLiteratureReview()
  const [isLoading, setIsLoading] = useState(!!id);
  const [error, setError] = useState("");
  const [queryTerms, setQueryTerms] = useState("");
  const [question, setQuestion] = useState(workflowData.question || "");
  const [searchFilters, setSearchFilters] = useState("")
  const [screenedArticles, setScreenedArticles] = useState([]);
  const [addedArticles, setAddedArticles] = useState([]);
  const [isScreeningSectionHidden, setIsScreeningSectionHidden] = useState(
    false
  );
  const [status, setStatus] = useState("");
  const [isAddFromSearchOpen, setIsAddFromSearchOpen] = useState(false);
  const [showLargeSearchWarning, setShowLargeSearchWarning] = useState(false);
  const [largeSearchArticleCount, setLargeSearchArticleCount] = useState(0);
  const [pollTrigger, setPollTrigger] = useState(0);
  const steps = getStepsForPage("paper-sources");

  const getArticleId = (a) => a.uniqueId ?? a.pubmed_id ?? a.title;

  const handleNext = () => {
    const articleUniqueIds = addedArticles
      .map((a) => a.uniqueId ?? a.pubmed_id)
      .filter((uid) => uid != null && uid !== "");
    setWorkflowData({ question, screenedArticles: addedArticles, articleUniqueIds });
    navigateToStep("abstract-screening");
  };

  const handleAddSources = (selectedSources) => {
    setAddedArticles((prev) => {
      const existingIds = new Set(prev.map((article) => getArticleId(article)));
      const newSources = selectedSources.filter(
        (s) => !existingIds.has(getArticleId(s))
      );
      const merged = [...prev, ...newSources];
      const articleUniqueIds = merged
        .map((a) => a.uniqueId ?? a.pubmed_id)
        .filter((id) => id != null && id !== "");
      saveAddedArticles(id, articleUniqueIds).catch((err) =>
        console.error("Failed to save added articles", err)
      );
      return merged;
    });
  };

  const handleDeleteArticle = (articleToDelete) => {
    const deleteId = getArticleId(articleToDelete);
    const updated = addedArticles.filter(
      (article) => getArticleId(article) !== deleteId
    );
    setAddedArticles(updated);
    const articleUniqueIds = updated
      .map((a) => a.uniqueId ?? a.pubmed_id)
      .filter((uid) => uid != null && uid !== "");
    saveAddedArticles(id, articleUniqueIds).catch((err) =>
      console.error("Failed to save after deleting article", err)
    );
  };

  const handleDeleteAllArticles = () => {
    if (!window.confirm("Delete all articles")) {
      return;
    }
    setAddedArticles([]);
    saveAddedArticles(id, []).catch((err) =>
      console.error("Failed to save after deleting all articles", err)
    );
  };

  useEffect(() => {
    if (!id) {
      setIsLoading(false);
      return;
    }

    const intervalId = setInterval(async () => {
      try {
        setIsLoading(true);
        const data = await fetchSources(id);
        setStatus(data.status);
        if (data.question) {
          setQuestion(data.question);
        }

        if (data.litreviewtitle) {
          setWorkflowData({ litreviewtitle: data.litreviewtitle });
        }

        if (data.status === "Gathering Sources Completed") {
          setQuestion(data.question);
          setWorkflowData({ litreviewtitle: data.litreviewtitle || data.question });
          setQueryTerms(data.query);
          if (data.searchFilters) {
            setSearchFilters(data.searchFilters)
          }
          if (data.screenedArticles) {
            const sortedArticles = data.screenedArticles.sort(
              (a, b) =>
                (a.gradeData?.GradeNumber || 0) -
                (b.gradeData?.GradeNumber || 0)
            );
            setScreenedArticles(sortedArticles);
          }
          if (data.addedArticles) {
            const sortedAddedArticles = data.addedArticles.sort((a, b) => (a.gradeData?.GradeNumber || 0) -
              (b.gradeData?.GradeNumber || 0))
            setAddedArticles(sortedAddedArticles)
          }
          setShowLargeSearchWarning(false);
          setIsLoading(false);
          clearInterval(intervalId);
        } else if (data.status === "large_search_warning") {
          if (data.query) setQueryTerms(data.query);
          if (data.question) setQuestion(data.question);
          setLargeSearchArticleCount(data.totalAbstracts || 0);
          setShowLargeSearchWarning(true);
          setIsLoading(false);
          clearInterval(intervalId);
        } else if (data.status === "Error") {
          setError(data.error || "An error occurred");
          if (data.question) setQuestion(data.question)
          if (data.litreviewtitle || data.question) setWorkflowData({ litreviewtitle: data.litreviewtitle || data.question })
          if (data.query) setQueryTerms(data.query)
          setShowLargeSearchWarning(false);
          setIsLoading(false);
          clearInterval(intervalId);
        }
      } catch (error) {
        console.error("Error fetching job status:", error);
        setError("Failed to fetch job status");
        clearInterval(intervalId);
        setIsLoading(false);
      }
    }, 1000);

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, pollTrigger, setWorkflowData]);

  useEffect(() => {
    if (workflowData.question) {
      setQuestion((prev) => prev || workflowData.question);
    }
  }, [workflowData.question]);
  const hasResults = !isLoading && !error && addedArticles.length > 0;
  const isGathering = isLoading && !showLargeSearchWarning;

  const handleProceedWithAll = async () => {
    try {
      setShowLargeSearchWarning(false);
      setIsLoading(true);
      await proceedLargeSearch(id);
      setPollTrigger((prev) => prev + 1);
    } catch (err) {
      console.error("Error proceeding with large search:", err);
      setError(err.message || "Failed to proceed with search");
      setIsLoading(false);
    }
  };

  const mainContent = (
    <div className={`paper-sources-content${isGathering ? " paper-sources-content--loading" : ""}`}>
      {showLargeSearchWarning && (
        <div className="large-search-warning">
          <div className="large-search-warning-icon">⚠️</div>
          <div className="large-search-warning-body">
            <p className="large-search-warning-title">
              Large search result: {largeSearchArticleCount.toLocaleString()} articles found
            </p>
            <p className="large-search-warning-text">
              Processing this many articles may take a long time. You can narrow your search by setting a maximum number of abstracts in the filter section, or proceed with all articles.
            </p>
            <div className="large-search-warning-actions">
              <button
                className="large-search-btn large-search-btn-primary"
                onClick={() => setIsAddFromSearchOpen(true)}
              >
                Narrow search
              </button>
              <button
                className="large-search-btn large-search-btn-secondary"
                onClick={handleProceedWithAll}
              >
                Proceed with all articles
              </button>
            </div>
          </div>
        </div>
      )}
      <div className={`source-selection-cards${isGathering ? " source-selection-cards--loading" : ""}`}>
        <div className={`source-card${isGathering ? " source-card--loading" : ""}`}>
          <h2 className="source-card-title">Select from MedRPT Search</h2>
          {isGathering ? (
            <div className="paper-sources-loading" role="status" aria-live="polite">
              <CircularProgress sx={{ color: "#3b7b3f" }} size={48} />
              <p className="paper-sources-loading-message">
                {status || "Loading relevant search results"}
              </p>
            </div>
          ) : (
            <>
              {error ? (
                <p className="source-card-status">{error}</p>
              ) : (
                <p className="source-card-status">
                  {screenedArticles.length > 0
                    ? `${screenedArticles.length} search results available`
                    : "No search results"}
                </p>
              )}
              <button
                className="source-card-button"
                onClick={() => setIsAddFromSearchOpen(true)}
              >
                <span className="source-card-icon">🔍</span>
                Add from search
              </button>
            </>
          )}
        </div>
      </div>

      {!isGathering && (hasResults ? (
        <>
          <div className="paper-sources-header">
            <span className="paper-count">
              {addedArticles.length} source{addedArticles.length === 1 ? "" : "s"} added for screening
            </span>
            <button
              type="button"
              className="delete-all-button"
              onClick={handleDeleteAllArticles}
            >
              <DeleteIcon sx={{ fontSize: 16 }} />
              Delete all
            </button>
          </div>
          <div className="papers-list">
            {addedArticles.map((article) => {
              const authors = Array.isArray(article.authors)
                ? article.authors.join(", ")
                : article.authors || "Unknown authors";

              return (
                <div key={getArticleId(article)} className="paper-item">
                  <div className="paper-details">
                    <h3 className="paper-title">{article.title}</h3>
                    <p className="paper-authors">{authors}</p>
                    <div className="paper-meta">
                      {article.journalTitle && (
                        <span className="paper-meta-item">
                          <MenuBookIcon
                            sx={{
                              fontSize: 14,
                              color: "#666",
                              mr: 0.5,
                              verticalAlign: "middle",
                            }}
                          />
                          {article.journalTitle},
                        </span>
                      )}
                      {article.publicationDate && (
                        <span className="paper-meta-item">
                          {article.publicationDate},
                        </span>
                      )}
                      {article.citationCount !== undefined &&
                        article.citationCount !== null && (
                          <span className="paper-meta-item">
                            {article.citationCount} citations,
                          </span>
                        )}
                      {article.doi !== null && (
                        <DoiButton doi={article.doi} />
                      )}
                    </div>
                  </div>
                  <IconButton
                    className="paper-delete-button"
                    onClick={() => handleDeleteArticle(article)}
                    size="small"
                    aria-label="Remove article"
                    sx={{ color: "#999", "&:hover": { color: "#d32f2f" } }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div className="no-sources-message">
          <div className="papers-header">
            <span className="paper-label">Articles</span>
            <span className="no-sources-text">No sources added</span>
          </div>
          <div className="no-sources-content">
            <p className="no-sources-title">No sources added</p>
            <p className="no-sources-subtitle">
              Open Add from search to choose articles for screening.
            </p>
          </div>
        </div>
      ))}
    </div>
  );

  const sidebar = (
    <div className="paper-sources-sidebar">
      <div className="sidebar-section">
        <h3 className="sidebar-title">
          Articles{" "}
          <span
            className="sidebar-icon"
            onClick={(e) => {
              e.stopPropagation();
              setIsScreeningSectionHidden(false);
            }}
          >
            ℹ️
          </span>
        </h3>
      </div>

      {!isScreeningSectionHidden && (
        <div className="sidebar-section">
          <div className="sidebar-header">
            <h4>Add articles for screening</h4>
          </div>
          <div className="sidebar-content">
            <p>
              Open <strong>Add from search</strong> to review search results
              for your research question. Select the articles you want, then
              add them to this list.
            </p>
            <p>
              MedRPT starts with relevant article suggestions. You can also
              update the search criteria to get a different set of results.
            </p>
            <p>
              Screen abstracts continues with every article on this list. Use
              the trash icon to drop one, or Delete all to start over. You
              need at least one article to continue.
            </p>
            <a
              href="#"
              className="sidebar-link"
              onClick={(e) => {
                e.preventDefault();
                setIsScreeningSectionHidden(true);
              }}
            >
              Hide
            </a>
          </div>
        </div>
      )}

      {question && (
        <div className="sidebar-section">
          <h4>Research question</h4>
          <p className="research-question">{question}</p>
        </div>
      )}
    </div>
  )

  return (
    <>
      <LiteratureReviewLayout
        projectTitle={workflowData.litreviewtitle || question || "Paper sources"}
        editableTitle={!!id}
        steps={steps}
        mainContent={mainContent}
        sidebar={sidebar}
        onNext={handleNext}
        nextLabel="Screen abstracts"
        showBack={false}
        showNext={!isLoading && !error && addedArticles.length > 0}
      />
      <AddFromSearch
        open={isAddFromSearchOpen}
        onClose={() => setIsAddFromSearchOpen(false)}
        query={queryTerms}
        question={question}
        searchFilters={searchFilters}
        screenedArticles={screenedArticles}
        onAddSources={handleAddSources}
        status={status}
        setScreenedArticles={setScreenedArticles}
        incomingError={error}
        onSearchUpdated={() => { setAddedArticles([]); setShowLargeSearchWarning(false); }}
        onQuestionUpdated={(newQuestion) => {
          if (newQuestion) setQuestion(newQuestion);
        }}
        onQueryUpdated={(newQuery) => {
          if (newQuery) setQueryTerms(newQuery);
        }}
        onLargeSearchWarning={(count) => {
          setLargeSearchArticleCount(count);
          setShowLargeSearchWarning(true);
        }}
      />
    </>
  );
};

export default PaperSources;
